import { useRealtimeAi } from "@/hooks/use-realtime-ai";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Bot,
  Clock,
  Flag,
} from "lucide-react";
import { apiGet } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { localCalendarDate, calendarDateInTimezone } from "./planning-date";
import { cn } from "@/lib/utils";

interface Task {
  id: string;
  title: string;
  status: string;
  dueDate: string | null;
  projectId?: string;
}
interface CalendarData {
  dailyCapacityMinutes: number | null;
  projects: {
    project: { id: string; name: string };
    work: { overdue: Task[]; dueToday: Task[]; upcoming: Task[] };
    deadlines: (Task & { assignees: { userId: string }[] })[];
    overdueCount: number;
    plan: {
      slots: {
        id: string;
        slotDate: string;
        plannedMinutes: number | null;
        task: Task;
      }[];
    };
  }[];
}
export function PersonalCalendar() {
  useRealtimeAi("");
  const fr = useI18n().language === "fr";
  const [date, setDate] = useState(localCalendarDate);
  const [view, setView] = useState<"week" | "month">("week");
  const settings = useQuery({
    queryKey: ["assistant-settings"],
    queryFn: () =>
      apiGet<{ settings: { timezone: string } }>("/ai/assistant/settings"),
  });
  const timezone =
    settings.data?.settings.timezone ??
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  useEffect(() => {
    setDate(calendarDateInTimezone(new Date(), timezone));
  }, [timezone]);
  const rangeStart = view === "month" ? date.slice(0, 7) + "-01" : date;
  const dayCount =
    view === "month"
      ? new Date(
          Number(date.slice(0, 4)),
          Number(date.slice(5, 7)),
          0,
        ).getDate()
      : 7;
  const query = useQuery({
    queryKey: ["assistant-calendar", rangeStart, timezone, dayCount],
    queryFn: () =>
      apiGet<CalendarData>(
        `/ai/assistant/calendar?${new URLSearchParams({ date: rangeStart, timezone, days: String(dayCount) })}`,
      ),
    refetchInterval: 15000,
  });
  const days = Array.from({ length: dayCount }, (_, index) => {
    const day = new Date(rangeStart + "T12:00:00Z");
    day.setUTCDate(day.getUTCDate() + index);
    return day.toISOString().slice(0, 10);
  });
  const move = (amount: number) => {
    const day = new Date(date + "T12:00:00Z");
    if (view === "month") {
      day.setUTCDate(1);
      day.setUTCMonth(day.getUTCMonth() + amount);
    } else day.setUTCDate(day.getUTCDate() + amount * 7);
    setDate(day.toISOString().slice(0, 10));
  };
  const overdue =
    query.data?.projects.reduce(
      (count, group) => count + group.overdueCount,
      0,
    ) ?? 0;
  return (
    <section
      id="personal-calendar"
      className="rounded-xl backdrop-blur-md bg-white/5 border border-white/10 p-4 sm:p-6 space-y-4"
    >
      <div className="flex flex-wrap items-center gap-3">
        <CalendarDays className="h-5 w-5 text-primary" />
        <h2 className="flex-1 text-lg font-semibold">
          {fr ? "Mon calendrier" : "My calendar"}
        </h2>
        <div
          className="flex rounded-lg border border-white/10 p-1"
          role="group"
          aria-label={fr ? "Vue du calendrier" : "Calendar view"}
        >
          {(["week", "month"] as const).map((mode) => (
            <button
              key={mode}
              aria-pressed={view === mode}
              onClick={() => setView(mode)}
              className={cn(
                "rounded-md px-3 py-1 text-sm",
                view === mode
                  ? "bg-primary/15 text-primary"
                  : "text-foreground-muted hover:bg-white/5",
              )}
            >
              {mode === "week"
                ? fr
                  ? "Semaine"
                  : "Week"
                : fr
                  ? "Mois"
                  : "Month"}
            </button>
          ))}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDate(calendarDateInTimezone(new Date(), timezone))}
        >
          {fr ? "Aujourd’hui" : "Today"}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label={
            view === "month"
              ? fr
                ? "Mois précédent"
                : "Previous month"
              : fr
                ? "Semaine précédente"
                : "Previous week"
          }
          onClick={() => move(-1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label={
            view === "month"
              ? fr
                ? "Mois suivant"
                : "Next month"
              : fr
                ? "Semaine suivante"
                : "Next week"
          }
          onClick={() => move(1)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <p className="text-sm font-medium" aria-live="polite">
        {new Date(rangeStart + "T12:00:00Z").toLocaleDateString(
          fr ? "fr" : "en",
          {
            month: "long",
            year: "numeric",
            ...(view === "week" ? { day: "numeric" as const } : {}),
            timeZone: "UTC",
          },
        )}
        {view === "week" &&
          ` – ${new Date(days[6] + "T12:00:00Z").toLocaleDateString(fr ? "fr" : "en", { month: "short", day: "numeric", timeZone: "UTC" })}`}
      </p>
      <p className="text-sm text-foreground-muted">
        {fr
          ? "Votre plan et vos échéances, partagés avec votre Assistant."
          : "Your plan and deadlines, shared with your Assistant."}{" "}
        <span>
          {fr
            ? "Tâches attribuées à vous, vos plans et tâches non attribuées dans vos projets."
            : "Tasks assigned to you, your plans and unassigned tasks in projects you own."}
        </span>{" "}
        {overdue > 0 && (
          <span className="text-amber-300">
            {overdue} {fr ? "en retard" : "overdue"}
          </span>
        )}
      </p>
      {query.error && (
        <p role="alert" className="text-sm text-destructive">
          {query.error.message}
        </p>
      )}
      {query.isLoading && (
        <p role="status" className="text-sm text-foreground-muted">
          {fr ? "Chargement du calendrier…" : "Loading calendar…"}
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-7 gap-2">
        {view === "month" &&
          Array.from(
            {
              length: (new Date(rangeStart + "T12:00:00Z").getUTCDay() + 6) % 7,
            },
            (_, index) => (
              <div
                key={`empty-${index}`}
                aria-hidden="true"
                className="hidden xl:block"
              />
            ),
          )}
        {days.map((day) => {
          const planned =
            query.data?.projects.flatMap((group) =>
              group.plan.slots
                .filter((slot) => slot.slotDate.slice(0, 10) === day)
                .map((slot) => ({ ...slot, project: group.project })),
            ) ?? [];
          const due =
            query.data?.projects.flatMap((group) =>
              group.deadlines
                .filter(
                  (task) =>
                    task.dueDate &&
                    calendarDateInTimezone(new Date(task.dueDate), timezone) ===
                      day,
                )
                .map((task) => ({ ...task, project: group.project })),
            ) ?? [];
          const minutes = planned.reduce(
            (sum, slot) => sum + (slot.plannedMinutes ?? 0),
            0,
          );
          return (
            <div
              key={day}
              data-calendar-date={day}
              className={cn(
                "rounded-xl border border-white/10 bg-white/[0.02] p-3 min-w-0",
                day === calendarDateInTimezone(new Date(), timezone) &&
                  "border-primary/35 bg-primary/5",
              )}
            >
              <p className="mb-3 text-sm font-medium">
                {new Date(day + "T12:00:00Z").toLocaleDateString(
                  fr ? "fr" : "en",
                  {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                    timeZone: "UTC",
                  },
                )}
              </p>
              <div className="space-y-2">
                {planned.map((slot) => (
                  <Link
                    key={slot.id}
                    data-calendar-kind="planned"
                    to={`/projects/${slot.project.id}?task=${slot.task.id}`}
                    className="block rounded-lg border border-primary/15 bg-primary/10 p-2 text-xs"
                  >
                    <span className="flex gap-1 text-primary">
                      <Clock className="h-3 w-3" />
                      {fr ? "Planifié" : "Planned"}
                      {slot.plannedMinutes && ` · ${slot.plannedMinutes} min`}
                    </span>
                    <span className="mt-1 block break-words">
                      {slot.task.title}
                    </span>
                    <span className="block mt-1 text-foreground-muted truncate">
                      {slot.project.name}
                    </span>
                  </Link>
                ))}
                {due.map((task) => (
                  <Link
                    key={task.id}
                    data-calendar-kind="deadline"
                    to={`/projects/${task.project.id}?task=${task.id}`}
                    className="block rounded-lg border border-amber-400/15 bg-amber-400/5 p-2 text-xs"
                  >
                    <span className="flex gap-1 text-amber-300">
                      <Flag className="h-3 w-3" />
                      {fr ? "Échéance" : "Deadline"}
                    </span>
                    <span className="mt-1 block break-words">{task.title}</span>
                    <span className="block mt-1 text-foreground-muted truncate">
                      {task.project.name}
                    </span>
                    {!task.assignees.length && (
                      <span className="mt-1 block text-foreground-muted">
                        {fr ? "Non attribuée" : "Unassigned"}
                      </span>
                    )}
                  </Link>
                ))}
                {!planned.length && !due.length && (
                  <p className="text-xs text-foreground-muted">
                    {fr ? "Libre" : "Open"}
                  </p>
                )}
              </div>
              {minutes > 0 && (
                <p
                  className={cn(
                    "mt-3 text-xs",
                    query.data?.dailyCapacityMinutes &&
                      minutes > query.data.dailyCapacityMinutes
                      ? "text-amber-300"
                      : "text-foreground-muted",
                  )}
                >
                  {minutes} min
                  {query.data?.dailyCapacityMinutes
                    ? ` / ${query.data.dailyCapacityMinutes}`
                    : ""}
                </p>
              )}
            </div>
          );
        })}
      </div>
      <Link
        to={`/assistant?prompt=${encodeURIComponent(fr ? "Aide-moi à planifier ma semaine à partir de mon calendrier, des échéances et des dépendances. Demande ma capacité si elle n’est pas définie." : "Help me plan my week using my calendar, deadlines and dependencies. Ask about capacity if it is not set.")}`}
        className="inline-flex items-center gap-2 text-sm text-primary"
      >
        <Bot className="h-4 w-4" />
        {fr ? "Planifier avec l’Assistant" : "Plan with Assistant"}
      </Link>
    </section>
  );
}
